import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useObservable } from 'react-use-observable';

import { Alert, Divider, Form, Select, Tooltip, Typography } from 'antd';

import MaskedInput from 'components/MaskedInput';
import { VERTICAL_URL } from 'settings';
import { numberToBRL } from 'v2/helpers/money';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { financialStatementService } from 'v2/services/financialStatement/statement';
import * as Yup from 'yup';

import { Icon } from '@eduzz/ui-antd-theme';

import { MIN_TRANSFER, MIN_TRANSFER_INTERNATIONAL } from './contants';
import type { TransferByInvoice } from '../../constants';

type selectOption = {
  label: string;
  value: string;
};

type Props = {
  loading: boolean;
  formIsValid: boolean;
  selectedBank: number;
  accountBankList: selectOption[];
  setSelectedBank: React.Dispatch<React.SetStateAction<number>>;
  valueToTransfer: string | null;
  setValueToTransfer: React.Dispatch<React.SetStateAction<string>>;
  setFormIsValid: (...args: any[]) => any;
  transferByInvoiceObj: TransferByInvoice;
  resetTransferByInvoiceObj: (...args: any[]) => any;
};

function TransferStep({
  loading,
  formIsValid,
  selectedBank,
  accountBankList,
  setSelectedBank,
  valueToTransfer,
  setValueToTransfer,
  setFormIsValid,
  transferByInvoiceObj,
  resetTransferByInvoiceObj
}: Props) {
  const [formMessageError, setFormMessageError] = useState(null);

  const [currentBalance] = useObservable(() => financialStatementService.getCurrentBalance(), []);
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);

  const balanceFormatted = typeof currentBalance === 'number' ? numberToBRL(currentBalance) : '-';

  const validationMinValue = user?.international_account ? MIN_TRANSFER_INTERNATIONAL : MIN_TRANSFER;
  const validationMinValueFormatted = numberToBRL(validationMinValue);
  const validationMaxValue = currentBalance;
  const validationMaxValueFormatted = balanceFormatted;

  const validateValue = async (value: string) => {
    const transferSchema = Yup.object().shape({
      valueToTransfer: Yup.number()
        .required()
        .min(validationMinValue, `Valor mínimo: ${validationMinValueFormatted}`)
        .max(validationMaxValue, `Valor máximo: ${validationMaxValueFormatted}`)
    });

    try {
      await transferSchema.validate({ valueToTransfer: value });
      setFormIsValid(true);
      setFormMessageError(null);
    } catch (err) {
      setFormIsValid(false);
      setFormMessageError(err.message);
    }
  };

  const handleInputChange = (value: string) => {
    resetTransferByInvoiceObj();
    validateValue(value);
    setValueToTransfer(value);
  };

  const getTransferByInvoiceMessage = () => {
    const transferByInvoiceFormatted = numberToBRL(transferByInvoiceObj.approx_amount);

    if (transferByInvoiceObj.approx_amount < validationMinValue) {
      return (
        <>
          <div>{`O valor mínimo para saque em nossa plataforma é de ${validationMinValueFormatted} e a soma de faturas com valor mais próximo ao solicitado é de ${transferByInvoiceFormatted}.`}</div>
          <div>Tente novamente com um valor acima disso e dará tudo certo</div>
        </>
      );
    }

    return `A soma das faturas com valor mais próximo ao solicitado é de ${transferByInvoiceFormatted}. Clique em "Transferir valor" para prosseguir com o valor apurado.`;
  };

  useEffect(() => setValueToTransfer(String(currentBalance)), [currentBalance, setValueToTransfer]);

  return (
    <div className='flex flex-col gap-4'>
      <Typography.Text type='secondary'>Informe o valor que deseja transferir para sua conta bancária.</Typography.Text>

      <div className='flex items-center justify-between gap-4'>
        <Typography.Text type='secondary'>Disponível para transferência:</Typography.Text>
        <Typography.Text>{validationMaxValueFormatted}</Typography.Text>
      </div>

      <Divider className='m-0' />

      <Form layout='vertical' className='flex flex-col gap-3'>
        <Form.Item
          label={
            <span className='inline-flex items-center gap-1'>
              Valor que deseja transferir:
              <Tooltip title='Valor que será transferido para a sua conta bancária' placement='top'>
                <span className='inline-flex cursor-help'>
                  <Icon.CircleHelp size={16} color='gray' />
                </span>
              </Tooltip>
            </span>
          }
          validateStatus={!formIsValid ? 'error' : undefined}
          help={!formIsValid && formMessageError ? formMessageError : undefined}
          className='mb-0'
        >
          <MaskedInput
            className='w-full'
            name='valueToTransfer'
            placeHolder='R$ 0,00'
            mask='money'
            disabled={typeof valueToTransfer === null || loading}
            value={valueToTransfer}
            onChange={handleInputChange}
            error={!formIsValid}
            maskCleanValue
            fullWidth
            allowClear
          />
        </Form.Item>

        {!!transferByInvoiceObj && <Alert type='warning' showIcon message={getTransferByInvoiceMessage()} />}

        {!!accountBankList && (
          <Form.Item label='Conta bancária' className='mb-0'>
            <Select
              value={selectedBank}
              onChange={value => setSelectedBank(value as number)}
              disabled={loading || !accountBankList}
              className='w-full'
              options={accountBankList?.map((option, index) => ({
                key: `${option.value}-${index}`,
                value: option.value,
                label: option.label
              }))}
            />
          </Form.Item>
        )}

        {!accountBankList && (
          <Alert
            type='warning'
            showIcon
            message={
              <>
                Falta pouco! Como você não tem nenhuma conta bancária ativa no momento, <BankAccountsLink />
              </>
            }
          />
        )}
      </Form>
    </div>
  );
}

function BankAccountsLink() {
  const userHasRemoveNewBankAccountsBeta = eduzzAccountsService.verifyUserHasBetaModule('remove_new_bank_accounts');

  if (userHasRemoveNewBankAccountsBeta) {
    return (
      <RouterLink to='/producer/profile/bankdata'>
        <Typography.Text strong>clique aqui e cadastre agora!</Typography.Text>
      </RouterLink>
    );
  }

  return (
    <Typography.Link href={`${VERTICAL_URL}/bank-accounts/`} target='_blank' rel='noopener noreferrer'>
      <Typography.Text strong>clique aqui e cadastre agora!</Typography.Text>
    </Typography.Link>
  );
}

export default TransferStep;
